import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectCardExpendTeamType, OperationRow } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import * as apiObject from 'api/apiObjects';
import * as apiWarehouse from 'api/apiWarehouses';
import { getProposalUseUsd } from 'modules/templates';
import vconst from 'utils/helpers/structs/vconst';
import { useObjectCard } from '../../../../context/ObjectCardProvider';
import { isOtherTeamIncome } from '../../../../model/isOtherTeamIncome';
import type { UploadDocumentFormValues } from '../../../../OffUploadDocument/UploadDocumentForm';

export const CHECKED_ROWS_COMMENT_MAX_LENGTH = 500;

export type CheckedRowsKind = 'products' | 'services' | 'other';

export interface CheckedRowsMenuProps {
    kind: CheckedRowsKind;
    checkedIds: number[];
    setCheckedIds: Dispatch<SetStateAction<number[]>>;
    setBusy: (busy: boolean) => void;
}

interface ActionRow {
    id: number;
    name: string;
    amount: number;
    canCancel: boolean;
}

const formatAmount = (value: number): string => {
    const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
    return String(rounded);
};

const lineAmount = (unitUsd: number, unitLocal: number, count: number, useUsd: boolean, rate: number) => {
    const qty = Number(count) || 0;
    if (useUsd) return unitUsd * qty;
    const localUnit = unitLocal !== 0 ? unitLocal : unitUsd * rate;
    return localUnit * qty;
};

const useCheckedRowsMenu = ({ kind, checkedIds, setCheckedIds, setBusy }: CheckedRowsMenuProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { vObject, fetchObject, isEditLocked } = useObjectCard();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const rate = Number(localSettings?.RATE?.current || 1) || 1;
    const useUsd = getProposalUseUsd(params.usdDefault, rate);
    const [showText, setShowText] = useState(false);
    const [paymentText, setPaymentText] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadSeed, setUploadSeed] = useState<Partial<UploadDocumentFormValues> | null>(null);
    const objectId = Number(vObject?.object?.ID || 0);

    const rows = useMemo(() => {
        const selected = new Set(checkedIds);
        if (kind === 'products') {
            const status = Number(vObject?.object?.objectStatus);
            const statusOk =
                status === vconst.objectPanding ||
                status === vconst.objectInWork ||
                (status === vconst.objectFinished && !isEditLocked);
            return (vObject?.operations || [])
                .filter((row): row is OperationRow => selected.has(Number(row.ID)))
                .map((row): ActionRow => ({
                    id: Number(row.ID),
                    name: String(row.prodName || '').trim(),
                    amount: lineAmount(
                        Number(row.operPriceSale) || 0,
                        Number(row.operPriceSaleRate) || 0,
                        Number(row.operCount) || 0,
                        useUsd,
                        rate,
                    ),
                    canCancel: Number(row.operStatus) === vconst.operStatApproved && statusOk,
                }));
        }

        return (vObject?.expendTeams || [])
            .filter((row): row is ObjectCardExpendTeamType =>
                kind === 'other' ? isOtherTeamIncome(row) : !isOtherTeamIncome(row),
            )
            .filter((row) => selected.has(Number(row.ID)))
            .map((row): ActionRow => {
                const count = Number(row.income_count ?? row.incomeCount) || 1;
                return {
                    id: Number(row.ID),
                    name: String(row.income_name ?? row.incomeName ?? '').trim(),
                    amount: lineAmount(
                        Number(row.income_price_sale ?? row.incomePriceSale) || 0,
                        Number(row.income_price_sale_rate ?? row.incomePriceSaleRate) || 0,
                        count,
                        useUsd,
                        rate,
                    ),
                    canCancel:
                        Number(row.income_status ?? row.incomeStatus) === vconst.incomeStatActive && !isEditLocked,
                };
            });
    }, [checkedIds, isEditLocked, kind, rate, useUsd, vObject]);

    const cancellable = rows.filter((row) => row.canCancel);
    const docNameKey =
        kind === 'products'
            ? 'checkedRows.docNameProducts'
            : kind === 'other'
              ? 'checkedRows.docNameOther'
              : 'checkedRows.docNameServices';

    const handleCancel = async () => {
        if (!cancellable.length) return;
        setBusy(true);
        const cancelled: number[] = [];
        let failed = false;
        let warrantyConflict = false;
        try {
            for (const row of cancellable) {
                const result = kind === 'products' ? await apiWarehouse.reversalOperationResult(row.id) : null;
                const ok = result ? result.stat : await apiObject.cancelExpendTeam(row.id);
                if (result?.errCode === 'WARRANTY_CAPACITY') warrantyConflict = true;
                if (ok) cancelled.push(row.id);
                else failed = true;
            }
            await fetchObject();
            if (failed || cancelled.length !== cancellable.length) {
                showToast(t(warrantyConflict ? 'messWarrantyCapacity' : 'common:messError'), 'danger');
            } else {
                showToast(t('messReversaled'), 'success');
            }
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setBusy(false);
            if (cancelled.length) {
                setCheckedIds((prev) => prev.filter((id) => !cancelled.includes(id)));
            }
        }
    };

    const openPaymentDialog = () => {
        const names = rows.map((row) => row.name).filter(Boolean).join(', ');
        setPaymentText(t('checkedRows.paymentFor', { names }).slice(0, CHECKED_ROWS_COMMENT_MAX_LENGTH));
        setShowText(true);
    };

    const handleCreateDocument = () => {
        const amount = rows.reduce((sum, row) => sum + row.amount, 0);
        setUploadSeed({
            proposalName: t(docNameKey),
            generationComment: paymentText.slice(0, CHECKED_ROWS_COMMENT_MAX_LENGTH),
            proposalAmount: formatAmount(amount),
        });
        setShowText(false);
        setUploadOpen(true);
    };

    const setPaymentTextLimited = (value: string) => {
        setPaymentText(value.slice(0, CHECKED_ROWS_COMMENT_MAX_LENGTH));
    };

    return {
        objectId,
        showText,
        setShowText,
        paymentText,
        setPaymentText: setPaymentTextLimited,
        uploadOpen,
        setUploadOpen,
        uploadSeed,
        cancellableCount: cancellable.length,
        handleCancel,
        openPaymentDialog,
        handleCreateDocument,
    };
};

export default useCheckedRowsMenu;
